// useAlert.ts
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

// Create a MySwal instance
const MySwal = withReactContent(Swal);

// Basic Alert with dynamic text
export const showBasicAlert = (text: string) => {
  MySwal.fire({
    text: text,
    confirmButtonText: "OK",
  });
};

// Alert with dynamic title and text
export const showAlertWithTitle = (title: string, text: string) => {
  MySwal.fire({
    title: title,
    text: text,
    confirmButtonText: "OK",
  });
};

// Alert with dynamic footer content
export const showFooterAlert = (
  title: string,
  text: string,
  footer: string
) => {
  MySwal.fire({
    title: title,
    text: text,
    footer: footer,
  });
};

// Alert with dynamic position
export const showPositionAlert = (
  position: "top-start" | "top-end" | "bottom-start" | "bottom-end",
  text: string
) => {
  MySwal.fire({
    text: text,
    position: position,
    showConfirmButton: false,
  });
};

// Custom timer alert with dynamic title and text
export const showCustomTimerAlert = (
  title: string,
  text: string,
  initialTime: number,
  endTime: number
) => {
  MySwal.fire({
    title: title,
    text: text,
    confirmButtonText: "OK",
    showConfirmButton: false,
    timer: initialTime,
    didOpen: () => {
      const timerInterval = setInterval(() => {
        const timerLeft = MySwal.getTimerLeft();
        if (timerLeft && timerLeft <= endTime) {
          clearInterval(timerInterval);
        }
      }, 100);
    },
    willClose: () => {
      MySwal.fire({
        title: "OK Button Clicked",
        icon: "success",
        confirmButtonText: "OK",
      });
    },
  });
};

// Chaining question modals with dynamic input and text
export const showQuestionAlert = () => {
  MySwal.fire({
    title: "Question 1",
    html: '<p>Chaining swal2 modals is easy</p><input id="swal-input1" class="swal2-input">',
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Next",
    cancelButtonText: "Cancel",
  }).then((result) => {
    if (result.isConfirmed) {
      const answer1 = (
        document.getElementById("swal-input1") as HTMLInputElement
      )?.value;
      showQuestion2Alert(answer1);
    }
  });
};

// Dynamically shows questions and answers in chained alerts
export const showQuestion2Alert = (answer1: string) => {
  MySwal.fire({
    title: "Question 2",
    html: `<p>Your answer from Question 1: <strong>${answer1}</strong></p><input id="swal-input2" class="swal2-input" placeholder="">`,
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Next",
    cancelButtonText: "Cancel",
  }).then((result) => {
    if (result.isConfirmed) {
      const answer2 = (
        document.getElementById("swal-input2") as HTMLInputElement
      )?.value;
      showQuestion3Alert(answer1, answer2);
    }
  });
};

// Dynamically shows third question and results
export const showQuestion3Alert = (answer1: string, answer2: string) => {
  MySwal.fire({
    title: "Question 3",
    html: `<p>Your answers from Questions 1 and 2: <strong>${answer1}</strong>, <strong>${answer2}</strong></p><input id="swal-input3" class="swal2-input" placeholder="">`,
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Next",
    cancelButtonText: "Cancel",
  }).then((result) => {
    if (result.isConfirmed) {
      const answer3 = (
        document.getElementById("swal-input3") as HTMLInputElement
      )?.value;
      MySwal.fire({
        title: "All Done",
        text: `Your answers: ["${answer1}", "${answer2}", "${answer3}"]`,
        confirmButtonText: "Lovely!",
      });
    }
  });
};

// Confirmation alert with dynamic text and title
export const showConfirmationAlert = (title: string, text: string) => {
  MySwal.fire({
    title: title,
    text: text,
    showCancelButton: true,
    confirmButtonColor: "#00ff00",
    confirmButtonText: "Yes, delete it!",
    cancelButtonColor: "#ff0000",
    cancelButtonText: "Cancel",
  }).then((result) => {
    if (result.isConfirmed) {
      MySwal.fire({
        title: "Deleted!",
        text: "Your file has been deleted.",
        confirmButtonText: "OK",
      });
    } else {
      MySwal.fire({
        title: "Cancelled",
        text: "Your imaginary file is safe :)",
        confirmButtonText: "OK",
      });
    }
  });
};

// Confirmation alert with dynamic text and title
// Confirmation alert with dynamic title, text, and assigned_by info

export const showTodoConfirmationAlert = (
  title: string,
  text: string,
  assignedBy: string,
  onClick: () => void,
  viewTaskRoute: string,
  profileImg?: string
) => {
  Swal.fire({
    title: `<div style="font-size: 24px; font-weight: bold; color: #2ecc71;">
              ✅ ${title}
            </div>`,
    html: `
      <div style="text-align: center; font-size: 16px; padding: 12px 0;">
        ${
          profileImg
            ? `<img src="${profileImg}" alt="Assigned By" style="width: 64px; height: 64px; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.1); margin-bottom: 12px;" />`
            : ""
        }
        <p style="margin: 12px 0;">
          <i class="ti ti-clipboard-text" style="color: #3498db; margin-right: 6px;"></i>
          <strong>Task:</strong> ${text}
        </p>
        <p style="margin-bottom: 0;">
          <i class="ti ti-user-check" style="color: #2ecc71; margin-right: 6px;"></i>
          <strong>Assigned by:</strong> ${assignedBy}
        </p>
      </div>
    `,
    background: "#fefefe",
    iconHtml: `<div style="font-size: 36px;">📌</div>`,
    showConfirmButton: true,
    confirmButtonText: "Got it!",
    confirmButtonColor: "#00b894",
    showDenyButton: true,
    denyButtonText: "View Task",
    denyButtonColor: "#0984e3",
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: {
      popup: "animated fadeInDown faster",
      confirmButton: "swal2-confirm custom-confirm-btn",
      denyButton: "swal2-deny custom-deny-btn",
    },
  }).then((result) => {
    if (result.isConfirmed) {
      onClick();
    } else if (result.isDenied) {
      window.location.href = viewTaskRoute;
    }
  });
};

// export const showTodoConfirmationAlert = (
//   title: string,
//   text: string,
//   onClick: () => void
// ) => {
//   MySwal.fire({
//     title,
//     text,
//     confirmButtonColor: "#008000",
//     confirmButtonText: "OK",
//     showCancelButton: false,
//   }).then((result) => {
//     if (result.isConfirmed) {
//       onClick();
//     }
//   });
// };
